// Assemble a workflow agent's READ-ONLY toolset from its declared
// capabilities. The control inverts vs a kernel: the agent drives and
// these are its toolkit. Everything here is read-only by construction
// EXCEPT MCP, which needs gating (the MCP→AgentTool adapter drops the
// `readOnlyHint` annotation, so v1 enforces read-only three ways:
//   1. capabilities.mcpServers is an explicit allow-list,
//   2. a write-verb name predicate drops obvious mutations,
//   3. the system prompt forbids any write.
// A hard `readOnlyHint` gate is the tracked follow-up — same posture
// as the kernel mcp_tool step. Analytics, python, render, and saved
// kernels are read-only by nature and need no gating.

import { Type } from "@earendil-works/pi-ai";
import { createAnalyticsQueryTool } from "./analyticsTool.js";
import { withScopeGuard } from "./scoutScope.js";
import { createPythonTool } from "./pythonTool.js";
import { createRenderBlockTool } from "./kernelRenderTool.js";
import { schemaTools } from "./schemaSearch.js";
import { collectMcpAgentTools } from "./mcp/index.js";
import { resolveKernelByCommandAsync } from "./kernelStore.js";
import { runKernel } from "./kernelRunner.js";
import { kernelErrorMessageForCurrentUser } from "./kernelErrorCopy.js";
import {
    RUN_SURFACES,
    createStoredRunContext,
} from "./kernelResolver.js";

// Tool-name fragments that signal a MUTATION. Tested against the
// UN-prefixed MCP tool name (after `<server>__`). Best-effort defence
// in depth — the allow-list + prompt are the primary gates.
const WRITE_VERB_RE = /(^|[_-])(create|update|delete|remove|destroy|save|post|comment|add|set|edit|write|upload|merge|close|cancel|archive|move|assign|insert|put|patch|send|approve|reject|attach|detach|invite)([_-]|$)/i;

export function isReadOnlyMcpToolName(prefixedName) {
    if (typeof prefixedName !== "string") return false;
    const idx = prefixedName.indexOf("__");
    const bare = idx >= 0 ? prefixedName.slice(idx + 2) : prefixedName;
    return !WRITE_VERB_RE.test(bare);
}

// Build the toolset for a run. `tenantId` is resolved by the caller.
// `getNamespaceKey` points every collaborating per-namespace store
// (pyodide namespace, toolResultStore, renderTracker) at this run's
// isolated key so nothing leaks across runs or from chats.
//
// A scoped run's analytics tool carries the scope guard (scoutScope.js):
// queries that never reference the scope's ids come back flagged in the
// result the agent reads (and in the persisted run log), and onScopeWarning
// lets the runner count them on the run record. The prompt's STAY SCOPED
// rule is intent; the guard is the mechanical check behind it.
export async function buildWorkflowToolset({ workflow, tenantId, getNamespaceKey, scope = null, onScopeWarning = null }) {
    const cap = workflow?.capabilities || {};
    const tools = [];

    if (cap.analytics !== false) {
        tools.push(withScopeGuard(createAnalyticsQueryTool({ getNamespaceKey }), {
            scopePath: scope,
            onWarning: onScopeWarning || undefined,
        }));
        tools.push(...schemaTools);
    }
    if (cap.python !== false) {
        // No `rehydrate`: a run is short-lived with its own namespace, so
        // there are no evicted prior-chat rows to recover. Same-run
        // from_tool_calls refs resolve through the shared namespace key.
        tools.push(createPythonTool({
            getNamespaceKey,
            runContext: createStoredRunContext({
                surface: RUN_SURFACES.BACKGROUND,
            }),
        }));
    }

    // render_block is always available — it's how a render-deliverable
    // workflow emits its artifact. Read-only (it only surfaces a spec).
    tools.push(createRenderBlockTool({ getNamespaceKey }));

    // Read-only MCP tools from allow-listed servers only.
    const servers = Array.isArray(cap.mcpServers) ? cap.mcpServers : [];
    if (servers.length && tenantId) {
        let mcpTools = [];
        try {
            mcpTools = await collectMcpAgentTools(tenantId);
        } catch (err) {
            console.warn("[workflowTools] MCP tool collection failed:", err);
        }
        const allow = new Set(servers);
        for (const t of mcpTools) {
            const prefix = typeof t?.name === "string" ? t.name.split("__")[0] : "";
            if (!allow.has(prefix)) continue;            // server not allow-listed
            if (!isReadOnlyMcpToolName(t.name)) continue; // looks like a write — drop it
            tools.push(t);
        }
    }

    // Saved kernels as callable read-only tools. Each runs a
    // deterministic (or agentic-but-tool-less) recipe and hands the
    // agent its result to reason over.
    const kernelCmds = Array.isArray(cap.kernels) ? cap.kernels : [];
    for (const cmd of kernelCmds) {
        try {
            const resolvedDefinition = await resolveKernelByCommandAsync(tenantId, cmd);
            if (resolvedDefinition) tools.push(makeKernelTool(resolvedDefinition));
        } catch (error) {
            // One stale/missing cloud definition cannot prevent the workflow
            // from assembling its remaining read-only tools.
            console.warn(`[workflowTools] saved kernel /${cmd} could not be resolved; skipping:`, error);
        }
    }

    return tools;
}

function makeKernelTool(resolvedDefinition) {
    const kernel = resolvedDefinition.body;
    const runContext = createStoredRunContext({
        surface: RUN_SURFACES.BACKGROUND,
    });
    return {
        name: `kernel__${kernel.command}`,
        label: `Run /${kernel.command}`,
        description:
            `Run the saved kernel "/${kernel.command}"${kernel.title ? ` (${kernel.title})` : ""} and return its result rows. ` +
            "Read-only analysis recipe. Pass `params` to override its declared parameters.",
        parameters: Type.Object({
            params: Type.Optional(Type.Unknown({
                description: "Object of parameter overrides for the kernel (names → values). Omit to use the kernel's defaults.",
            })),
        }),
        async execute(_id, args) {
            const params = args && typeof args.params === "object" && !Array.isArray(args.params) ? args.params : {};
            const res = await runKernel({
                resolvedDefinition,
                runContext,
                params,
            });
            if (!res.ok) {
                const message = await kernelErrorMessageForCurrentUser(res.error);
                return {
                    content: [{ type: "text", text: `kernel /${kernel.command} failed: ${message}` }],
                };
            }
            const data = res.renderData;
            const text = safeJson(data);
            return {
                content: [{ type: "text", text: text.length > 4000 ? text.slice(0, 4000) + "\n…(truncated)" : text }],
                details: { kind: "kernelBlock", spec: res.renderSpec, data },
            };
        },
    };
}

function safeJson(value) {
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}
